import { z } from 'zod';
import { amountMinorSchema, isoTimestampSchema, uuidSchema } from './common';

export const ORDER_ITEM_STATUSES = [
  'draft',
  'sent',
  'preparing',
  'ready',
  'served',
  'cancelled',
] as const;
export const orderItemStatusSchema = z.enum(ORDER_ITEM_STATUSES);
export type OrderItemStatus = z.infer<typeof orderItemStatusSchema>;

export const ORDER_TYPES = ['dine_in', 'takeaway', 'delivery'] as const;
export const orderTypeSchema = z.enum(ORDER_TYPES);
export type OrderType = z.infer<typeof orderTypeSchema>;

export const SALES_CHANNELS = ['walk_in', 'gofood', 'grabfood', 'shopeefood', 'other'] as const;
export const salesChannelSchema = z.enum(SALES_CHANNELS);
export type SalesChannel = z.infer<typeof salesChannelSchema>;
export const DELIVERY_CHANNELS = ['gofood', 'grabfood', 'shopeefood', 'other'] as const;

/**
 * Who/where an order is for. Dine-in orders have a table; takeaway and delivery orders
 * have none and are identified by customer name and/or platform order number (ADR-0017).
 */
export const orderContextShape = {
  order_type: orderTypeSchema,
  channel: salesChannelSchema,
  /** Platform order number (delivery). */
  external_ref: z.string().nullable(),
  customer_name: z.string().nullable(),
  table_session_id: uuidSchema.nullable(),
  table_id: uuidSchema.nullable(),
  table_name: z.string().nullable(),
};

export const orderItemModifierSchema = z.object({
  group_id: uuidSchema,
  group_name: z.string(),
  option_id: uuidSchema,
  option_name: z.string(),
  price_delta_minor: amountMinorSchema,
});

export const orderItemSchema = z.object({
  id: uuidSchema,
  menu_item_id: uuidSchema,
  name: z.string(),
  /** Whole portions. Stored as quantity_scaled/scale internally (spec §6.4). */
  quantity: z.number().int().positive(),
  unit_price_minor: amountMinorSchema,
  line_total_minor: amountMinorSchema,
  modifiers: z.array(orderItemModifierSchema),
  notes: z.string().nullable(),
  seat: z.number().int().nullable(),
  status: orderItemStatusSchema,
  submission_no: z.number().int().nullable(),
  station_id: uuidSchema.nullable(),
  cancel_reason: z.string().nullable(),
  sent_at: isoTimestampSchema.nullable(),
  served_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const orderSchema = z.object({
  id: uuidSchema,
  order_number: z.string(),
  ...orderContextShape,
  status: z.enum(['open', 'closed', 'cancelled']),
  source: z.enum(['pos', 'service', 'guest']),
  currency: z.string(),
  items: z.array(orderItemSchema),
  /** Non-cancelled submitted items. */
  submitted_total_minor: amountMinorSchema,
  /** Draft items not yet sent to the kitchen. */
  draft_total_minor: amountMinorSchema,
  version: z.number().int(),
  created_at: isoTimestampSchema,
});
export type Order = z.infer<typeof orderSchema>;

export const orderListResponseSchema = z.object({ items: z.array(orderSchema) });

export const createOrderRequestSchema = z
  .object({
    order_type: orderTypeSchema.default('dine_in'),
    /** Required for dine-in; must be absent otherwise. */
    table_session_id: uuidSchema.optional(),
    /** Delivery: the platform. Takeaway: walk_in (default). */
    channel: salesChannelSchema.optional(),
    /** Delivery: the platform's order number (required). */
    external_ref: z.string().trim().min(1).max(40).optional(),
    customer_name: z.string().trim().min(1).max(80).optional(),
    source: z.enum(['pos', 'service']).default('pos'),
  })
  .superRefine((v, ctx) => {
    if (v.order_type === 'dine_in') {
      if (!v.table_session_id)
        ctx.addIssue({
          code: 'custom',
          path: ['table_session_id'],
          message: 'Required for dine-in.',
        });
      if (v.channel && v.channel !== 'walk_in')
        ctx.addIssue({ code: 'custom', path: ['channel'], message: 'Dine-in is always walk-in.' });
      return;
    }
    if (v.table_session_id)
      ctx.addIssue({
        code: 'custom',
        path: ['table_session_id'],
        message: 'Only dine-in orders have a table.',
      });
    if (v.order_type === 'delivery') {
      if (!v.channel || v.channel === 'walk_in')
        ctx.addIssue({
          code: 'custom',
          path: ['channel'],
          message: 'Choose the delivery platform.',
        });
      if (!v.external_ref)
        ctx.addIssue({
          code: 'custom',
          path: ['external_ref'],
          message: 'Enter the platform order number.',
        });
    } else if (v.channel && v.channel !== 'walk_in') {
      ctx.addIssue({
        code: 'custom',
        path: ['channel'],
        message: 'Takeaway is walk-in; use delivery for platforms.',
      });
    }
  });

export const orderListQuerySchema = z.object({
  status: z.enum(['open', 'closed']).optional(),
  order_type: orderTypeSchema.optional(),
});

export const addOrderItemRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  menu_item_id: uuidSchema,
  quantity: z.number().int().min(1).max(99),
  modifier_option_ids: z.array(uuidSchema).max(40).default([]),
  notes: z.string().trim().max(200).nullable().default(null),
  seat: z.number().int().min(1).max(50).nullable().default(null),
});

export const updateOrderItemRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  quantity: z.number().int().min(1).max(99).optional(),
  notes: z.string().trim().max(200).nullable().optional(),
  seat: z.number().int().min(1).max(50).nullable().optional(),
});

export const cancelOrderItemRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  /** Required once the item has been sent to the kitchen. */
  reason: z.string().trim().min(1).max(200).optional(),
});

export const submitOrderRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});

export const serveOrderItemsRequestSchema = z.object({
  order_item_ids: z.array(uuidSchema).min(1).max(100),
});
